import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { Hint } from '@systemeio/ui'
import { GrowthbookFlagsEnum } from 'growthbook/types'
import debounce from 'lodash.debounce'
import { useLayoutEffect, useRef, useState } from 'react'
import Avatar from 'shared/components/header/components/avatar'
import { DesktopMenu } from 'shared/components/header/components/desktop-menu'
import SideMenu from 'shared/components/header/components/side-menu'
import SioButton from 'shared/components/header/components/sio-button'
import WorkspaceSelector from 'shared/components/header/components/workspace-selector'
import { DesktopLanguageSwitcher } from 'shared/components/language-switcher/desktop-language-switcher'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { getAccountRestrictedLink } from 'shared/utils/get-account-restricted-link'
import ButtonLikeLink from '../button-like-link'
import LinkWithoutPrefetch from '../link-without-prefetch'
import TransLoco from '../trans-loco'
import useSWR from 'swr'

export const useActualHeaderLayoutType = () => {
  const { data, mutate } = useSWR<boolean>('header-layout-type')

  return {
    isDesktop: data,
    setIsDesktop: (desktop: boolean) => mutate(desktop, false),
  }
}

function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const { isLoading, user, isRestrictedUser } = useUser()
  const { t } = useLocoTranslation()
  const navRef = useRef<HTMLDivElement>(null)
  const workspaceRef = useRef<HTMLDivElement>(null)
  const sideMenuRef = useRef<HTMLDivElement>(null)
  const desktopMenuRef = useRef<HTMLDivElement>(null)
  const avatarRef = useRef<HTMLDivElement>(null)

  const { setIsDesktop } = useActualHeaderLayoutType()

  useLayoutEffect(() => {
    const handleResize = debounce(
      () => {
        const nav = navRef.current
        const desktopMenu = desktopMenuRef.current
        const sideMenu = sideMenuRef.current
        const avatar = avatarRef.current
        const workspace = workspaceRef.current

        if (!desktopMenu || !sideMenu || !nav || !avatar || !workspace) return

        const showDesktopElements = () => {
          desktopMenu.classList.remove('hidden')
          avatar.classList.remove('hidden')
          workspace.classList.remove('hidden')

          desktopMenu.classList.add('flex')
          avatar.classList.add('flex')
          workspace.classList.add('flex')

          sideMenu.classList.add('hidden')
          sideMenu.classList.remove('flex')

          setIsDesktop(true)
        }

        const showMobileElements = () => {
          desktopMenu.classList.remove('flex')
          avatar.classList.remove('flex')
          workspace.classList.remove('flex')

          desktopMenu.classList.add('hidden')
          avatar.classList.add('hidden')
          workspace.classList.add('hidden')

          sideMenu.classList.remove('hidden')
          sideMenu.classList.add('flex')

          setIsDesktop(false)
        }

        if (document.body.getBoundingClientRect().width < 1024) {
          showMobileElements()
          return
        }

        if (nav.scrollWidth > nav.offsetWidth) {
          showMobileElements()
        } else {
          showDesktopElements()

          if (nav.scrollWidth > nav.offsetWidth) {
            showMobileElements()
          }
        }
      },
      300,
      {
        leading: true,
        trailing: true,
      },
    )

    handleResize()

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isLoading, user?.dashboardLocale])

  return (
    <nav ref={navRef} className="top-0 z-10">
      <div className="text-background-secondary z-10 mx-auto flex h-[69px] w-full items-center justify-between gap-4 bg-[rgb(var(--background-header))] px-4 sm:px-6 md:px-14">
        <div className="flex items-center gap-4">
          <SioButton />

          <div ref={workspaceRef} className="flex">
            <WorkspaceSelector />
          </div>
        </div>

        <div ref={desktopMenuRef} className="ml-auto flex h-full">
          <DesktopMenu />
        </div>

        <div ref={avatarRef} className="flex h-full shrink-0 items-center justify-around gap-4">
          <Avatar />
          <DesktopLanguageSwitcher />
        </div>

        <div ref={sideMenuRef} className="hidden">
          <SideMenu isOpen={isOpen} setIsOpen={setIsOpen} />
        </div>
      </div>

      {user && isRestrictedUser && (
        <div className="bg-danger/15 border-b-danger text-danger z-10 flex min-h-4 w-full items-center justify-center border-b px-4 py-1 sm:px-6 md:px-14">
          <span className={'fill-danger flex flex-wrap items-center'}>
            <Hint
              className="flex-wrap"
              label={
                <ButtonLikeLink
                  onClick={() => window.open(getAccountRestrictedLink(user?.dashboardLocale))}
                >
                  {t('global.read_more')}
                </ButtonLikeLink>
              }
            >
              <TransLoco
                t={t}
                i18nKey={'dashboard.notifications.account_restricted'}
                components={{
                  a: <LinkWithoutPrefetch href={`/profile/user-plan`} className={'primary-link'} />,
                }}
              />
            </Hint>
          </span>
        </div>
      )}
    </nav>
  )
}

export default Header
